// The Manuscript side of a row: the nodes a Block was made from, set as the
// book sets them, with the word diff's removals painted over the same text.
//
// The two layers come from different places — the emphasis from the node's
// own markers, the removals from the pair's diff — and they overlap, so both
// are reduced to ranges over the node's stripped text and painted in one walk
// (spans.ts). Rendering the diff parts directly would have cost the emphasis,
// and rendering the node directly would have cost the diff.

import type { AlignedNode, NodeType } from "@contract";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../../../components/cx";
import type { NodeSpan } from "./diff";
import { readEmphasis } from "./emphasis";
import { paintSegments, type Range, rangesWithin, type Segment } from "./spans";

/** How each Manuscript node type is set, in the mockups' terms. */
const NODE_CLASS: Readonly<Record<NodeType, string>> = Object.freeze({
  heading: "font-serif text-title-sm font-semibold text-ink",
  paragraph: "font-serif text-book text-ink",
  listItem: "font-serif text-book text-ink pl-5 -indent-3 before:content-['—__']",
  table: "font-mono text-data text-ink-2",
  footnote: "border-t border-line-soft pt-2.5 font-serif text-book-sm text-ink-2",
  figure: "font-serif text-book-sm text-ink-2",
  caption: "font-serif text-book-sm text-ink-2",
  pageBreak: "",
});

function EmphasisSpans({ segments, removedLabel }: { segments: Segment[]; removedLabel: string }) {
  let offset = 0;
  return (
    <>
      {segments.map((segment) => {
        const key = `${offset}-${segment.emphasis}-${segment.removed}`;
        offset += segment.text.length;
        const inner =
          segment.emphasis === "bold" ? (
            <strong className="font-semibold">{segment.text}</strong>
          ) : segment.emphasis === "italic" ? (
            <em>{segment.text}</em>
          ) : (
            segment.text
          );
        if (!segment.removed) return <span key={key}>{inner}</span>;
        return (
          <del key={key} className="bg-diff-del no-underline">
            <span className="sr-only">{removedLabel}: </span>
            {inner}
          </del>
        );
      })}
    </>
  );
}

/**
 * One node. A node with no place in the pair's source text was left out of
 * the Narration Script on purpose — a running head, a folio — so it is struck
 * through in the frame's own mono face rather than set as book text: it is
 * page furniture, and the Author should be able to tell that at a glance from
 * a text that only reads as removed.
 */
function ManuscriptNode({
  node,
  span,
  removals,
}: {
  node: AlignedNode;
  span: Range | null;
  removals: readonly Range[];
}) {
  const { t } = useTranslation("components");
  if (node.type === "pageBreak" || node.text === "") {
    return <hr data-node-type="pageBreak" className="my-1 border-0 border-t border-line-soft" />;
  }
  const { text, runs } = readEmphasis(node.text);
  if (!span) {
    return (
      <p
        data-node-id={node.id}
        data-furniture="true"
        className="m-0 text-center font-mono text-label text-ink-3 line-through"
      >
        {text}
      </p>
    );
  }
  const segments = paintSegments(text, runs, rangesWithin(removals, span));
  return (
    <p data-node-id={node.id} className={cx("m-0", NODE_CLASS[node.type])}>
      <EmphasisSpans segments={segments} removedLabel={t("diff.removed")} />
    </p>
  );
}

/**
 * Memoized on the row's identity: scrolling re-renders the rows that entered
 * the window, and a row that only moved has nothing to recompute.
 */
export const ManuscriptColumn = memo(function ManuscriptColumn({
  spans,
  removals,
}: {
  spans: readonly NodeSpan[];
  removals: readonly Range[];
}) {
  return (
    <div data-column="manuscript" className="flex min-w-0 flex-col gap-4">
      {spans.map(({ node, span }) => (
        <ManuscriptNode key={node.id} node={node} span={span} removals={removals} />
      ))}
    </div>
  );
});
